import { defineMessages } from 'react-intl'

/**
 * Canonical customer-facing labels for Luxury Escapes product verticals and
 * the LE program / partner-brand names. Keyed by camelCase identifiers so
 * config-driven surfaces can reference descriptors consistently in code.
 *
 * These are the standardised labels used across nav, footer, checkout, and
 * partner-brand surfaces — see `docs/product-taxonomy.md` for the full
 * glossary and guidance on adding new entries.
 *
 * Consumers call `intl.formatMessage` themselves, e.g.
 * `intl.formatMessage(productLabels.hotels)` or
 * `<FormattedMessage {...productLabels.hotels} />`.
 */
const productLabels = defineMessages({
  businessTravel: {
    id: '16puE9f3VvqdIi--y81T',
    defaultMessage: 'Business Travel',
  },
  carHire: {
    id: '8ZwA9-ddQCEbp-_wRZZa',
    defaultMessage: 'Car Hire',
  },
  accommodation: {
    id: 'mVLgncSoKp8uitszEim1',
    defaultMessage: 'Accommodation',
  },
  cruise: {
    id: '2uO1FQHYceKJz__Iw1cZ',
    defaultMessage: 'Cruise',
  },
  cruises: {
    id: 'gGAv1vuigbVBK6XhAtiL',
    defaultMessage: 'Cruises',
  },
  cudo: {
    id: 'Zwchbeoqwc48yfF2CWqO',
    defaultMessage: 'Cudo',
  },
  cvfrHolidays: {
    id: '3ot9et7DkSsaJ1fWnd_e',
    defaultMessage: 'CVFR Holidays',
  },
  deals: {
    id: 'o7y1P2ZJe2cveNsm8hIv',
    defaultMessage: 'Deals',
  },
  directMaldivesFlights: {
    id: 'KjN2E89ePMc3CPA4jN-Z',
    defaultMessage: 'Direct Maldives Flights',
  },
  experience: {
    id: 'EJnlHihWvTGFZS1bA1sr',
    defaultMessage: 'Experience',
  },
  experiences: {
    id: 'kGv7JMD3Z3LNdQCA6K9r',
    defaultMessage: 'Experiences',
  },
  flight: {
    id: 'Vjsk1W1Xf2kkhhK4BhrF',
    defaultMessage: 'Flight',
  },
  flights: {
    id: 'rjkAgXNqcXA47CAL9sAD',
    defaultMessage: 'Flights',
  },
  giftCards: {
    id: 'Fdyo8GZ5KQkWvc75vhh_',
    defaultMessage: 'Gift Cards',
  },
  golf: {
    id: 'nuEE-728_wfga96BmVdb',
    defaultMessage: 'Golf',
  },
  groups: {
    id: 'a-Ry45gISOQOTFqvRBKz',
    defaultMessage: 'Groups',
  },
  holidays: {
    id: 'mpbKIx7c9XM79v5UeRYu',
    defaultMessage: 'Holidays',
  },
  hotel: {
    id: '4y8ctsZ46VsZDgOw8Efu',
    defaultMessage: 'Hotel',
  },
  home: {
    id: 'j-noXtNNi7Kqihb9Fr1h',
    defaultMessage: 'Home',
  },
  homesAndVillas: {
    id: '9FgjGNcJvHHrI0F8fEDp',
    defaultMessage: 'Homes & Villas',
  },
  hotels: {
    id: 'Du7wu76EAhcUNe7aYRrW',
    defaultMessage: 'Hotels',
  },
  hotelsAndResorts: {
    id: 'hlNqY6uTwK8zCnHbuT-t',
    defaultMessage: 'Hotels & Resorts',
  },
  inspiration: {
    id: 'SCxm4dKRDfsQ5WcVPKtr',
    defaultMessage: 'Inspiration',
  },
  leAgentHub: {
    id: 'YCr-jL_qfc-yJKZii6NH',
    defaultMessage: 'LE Agent Hub',
  },
  luxuryEscapes: {
    id: '5turM7CS4vZ9TvX2xPHc',
    defaultMessage: 'Luxury Escapes',
  },
  luxPlus: {
    id: 't1VsvOs4hSp9vtiZ85XS',
    defaultMessage: 'LuxPlus+',
  },
  maldivesBundles: {
    id: 'YVAOYUDUw_0ZiP7xa-Fs',
    defaultMessage: 'Maldives Bundles',
  },
  more: {
    id: 'iogTL25pepF33WNv_kzn',
    defaultMessage: 'More',
  },
  myEscapes: {
    id: 'H01E5qMD_amz1GJla5s9',
    defaultMessage: 'My Escapes',
  },
  purchaseCredits: {
    id: 'vo5zMX4Ce57R4qn3g5D5',
    defaultMessage: 'Purchase Credits',
  },
  referAFriend: {
    id: '1ibiA1tK4QuLynM1avt9',
    defaultMessage: 'Refer a Friend',
  },
  reviewAndPay: {
    id: 'untLzd7mr1r9ByuyOBhu',
    defaultMessage: 'Review & Pay',
  },
  scoopon: {
    id: '2U2DxbcGZdlBbHSmq7_M',
    defaultMessage: 'Scoopon',
  },
  shop: {
    id: 'h7dyYt-ykomT2vTkJyCO',
    defaultMessage: 'Shop',
  },
  societe: {
    id: 'yUDJ6xWCQUgwyOFcurqS',
    defaultMessage: 'Société',
  },
  support: {
    id: '9QaG60nE3rpbHlBuAlFK',
    defaultMessage: 'Support',
  },
  tour: {
    id: 'S4r_ssKptdgBBMwJCiu0',
    defaultMessage: 'Tour',
  },
  tours: {
    id: 'yOTXLZhiQJuObKxhBiJI',
    defaultMessage: 'Tours',
  },
  toursAndCruises: {
    id: 'PQNDf9pmC-Df0AIS_TSF',
    defaultMessage: 'Tours & Cruises',
  },
  transfersAndCars: {
    id: '4858klKHNvbzcJWrf3wV',
    defaultMessage: 'Transfers & Cars',
  },
  travelProtection: {
    id: 'qnb-VRE3XIXz11_ZWiFY',
    defaultMessage: 'Travel Protection',
  },
  travelshoot: {
    id: 'wR5CkMsrZ7fHOEvaoVXj',
    defaultMessage: 'Travelshoot',
  },
  treatme: {
    id: 'Xh20pzxYOzWaJyyHpS63',
    defaultMessage: 'Treatme',
  },
  treatMe: {
    id: 'x8tkc_CPqs1vW84wWzN0',
    defaultMessage: 'Treat Me',
  },
  ultraLux: {
    id: 'J7UdomXHkCph_5dLGjHm',
    defaultMessage: 'Ultra Lux',
  },
  visitUsInStore: {
    id: 'OJY-V8ElE34mYkQ3bxv6',
    defaultMessage: 'Visit Us In Store',
  },
  weddings: {
    id: 'NacytM3o5VYlYkVIKrLJ',
    defaultMessage: 'Weddings',
  },
})

/** Header logo screen-reader label: one `productLabels` entry per `config.BRAND`. */
export const navBrandLogoProductLabel = {
  luxuryescapes: productLabels.luxuryEscapes,
  cudotravel: productLabels.cudo,
  scoopontravel: productLabels.scoopon,
  treatmetravel: productLabels.treatme,
  dealstravel: productLabels.deals,
  leagenthub: productLabels.leAgentHub,
  leagenthub_cvfr: productLabels.cvfrHolidays,
} satisfies Record<App.BrandName, (typeof productLabels)[keyof typeof productLabels]>

export default productLabels
